import React, { useRef, useState } from "react";
import { useT } from "../i18n";
import { useViewWidth } from "../components/ViewWidthSlider";
import { importExcelFileFull } from "../utils/excelImporter";
import { parseKml, KmlPlacemark } from "../utils/kmlImporter";
import {
  parseLabeledPlaces,
  buildCoordIndex,
  lookupCoord,
  LabeledPlace,
} from "../utils/labeledPlacesImporter";
import {
  HEADERS,
  COL,
  appendRows,
  appendRowsWide,
  batchUpdateValues,
  cellRange,
  displayNameFormula,
  ensureSpreadsheet,
  extendHeaderRow,
  readAllRows,
  replaceSpreadsheetFromXlsx,
} from "../api/sheets";
import { Row } from "../types/sheets";

function extractDriveFileId(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = /[?&]id=([A-Za-z0-9_-]+)/.exec(url) || /\/file\/d\/([A-Za-z0-9_-]+)/.exec(url);
  return m ? m[1] : null;
}

interface AnalysisResult {
  matched: Array<{ pm: KmlPlacemark; pmIdx: number; id: string; xlsxIdx: number }>;
  unmatchedKml: KmlPlacemark[];
  unmatchedJson: LabeledPlace[];
  willCreate: Array<{ pm: KmlPlacemark; id: string }>;
  placemarksWithoutGps: number;
  uniqueUrls: number;
  driveUrls: number;
  nonDriveUrls: number;
  commonUrls: Set<string>;
}

export default function ImportView() {
  const t = useT();
  const { maxWidth, slider } = useViewWidth("viewWidth.import");
  const xlsxRef = useRef<HTMLInputElement>(null);
  const kmlRef = useRef<HTMLInputElement>(null);
  const jsonRef = useRef<HTMLInputElement>(null);
  const [xlsxFile, setXlsxFile] = useState<File | null>(null);
  const [kmlFile, setKmlFile] = useState<File | null>(null);
  const [jsonFile, setJsonFile] = useState<File | null>(null);
  const [xlsxRows, setXlsxRows] = useState<Row[] | null>(null);
  const [xlsxExtras, setXlsxExtras] = useState<{ headers: string[]; rows: string[][] }>({ headers: [], rows: [] });
  const [xlsxHasAllRequired, setXlsxHasAllRequired] = useState(false);
  const [xlsxIsCanonicalOrder, setXlsxIsCanonicalOrder] = useState(false);
  const [placemarks, setPlacemarks] = useState<KmlPlacemark[] | null>(null);
  const [labeledEntries, setLabeledEntries] = useState<LabeledPlace[] | null>(null);
  const [analysisError, setAnalysisError] = useState("");
  const [skipXlsx, setSkipXlsx] = useState(false);
  const [overwriteMode, setOverwriteMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [done, setDone] = useState(false);

  function appendLog(line: string) { setLog((l) => [...l, line]); }

  async function handleXlsxPick(file: File | null | undefined) {
    setXlsxFile(file || null);
    setXlsxRows(null);
    setXlsxExtras({ headers: [], rows: [] });
    setXlsxHasAllRequired(false);
    setXlsxIsCanonicalOrder(false);
    setOverwriteMode(false);
    setAnalysisError("");
    if (!file) return;
    try {
      const parsed = await importExcelFileFull(file);
      setXlsxRows(parsed.rows);
      setXlsxExtras({ headers: parsed.extraHeaders, rows: parsed.extraRows });
      setXlsxHasAllRequired(parsed.hasAllRequired);
      setXlsxIsCanonicalOrder(parsed.isCanonicalOrder);
    } catch (e) { setAnalysisError(`XLSX parse error: ${(e as Error).message}`); }
  }

  async function handleKmlPick(file: File | null | undefined) {
    setKmlFile(file || null);
    setPlacemarks(null);
    setAnalysisError("");
    if (!file) return;
    try {
      const text = await file.text();
      setPlacemarks(parseKml(text));
    } catch (e) { setAnalysisError(`KML parse error: ${(e as Error).message}`); }
  }

  async function handleJsonPick(file: File | null | undefined) {
    setJsonFile(file || null);
    setLabeledEntries(null);
    setAnalysisError("");
    if (!file) return;
    try {
      const text = await file.text();
      setLabeledEntries(parseLabeledPlaces(text));
    } catch (e) { setAnalysisError(`JSON parse error: ${(e as Error).message}`); }
  }

  const analysis: AnalysisResult | null = (() => {
    if (!placemarks || !labeledEntries) return null;
    if (!skipXlsx && !xlsxRows) return null;
    const idCol = COL["ID"];
    const coordToId = buildCoordIndex(labeledEntries);
    const idToXlsxIdx = new Map<string, number>();
    if (xlsxRows) {
      xlsxRows.forEach((r, i) => {
        const id = String(r[idCol] || "").trim();
        if (id && !idToXlsxIdx.has(id)) idToXlsxIdx.set(id, i);
      });
    }
    const matched: AnalysisResult["matched"] = [];
    const unmatchedKml: KmlPlacemark[] = [];
    const willCreate: AnalysisResult["willCreate"] = [];
    const usedIds = new Set<string>();
    const urlSet = new Set<string>();
    const urlCount = new Map<string, number>();
    let placemarksWithoutGps = 0;

    placemarks.forEach((pm, pmIdx) => {
      if (pm.lat == null || pm.lng == null) { placemarksWithoutGps++; unmatchedKml.push(pm); return; }
      const id = lookupCoord(coordToId, pm.lng, pm.lat);
      if (!id) { unmatchedKml.push(pm); return; }
      usedIds.add(id);
      const uniqInPm = new Set(pm.mediaUrls);
      uniqInPm.forEach((u) => { urlSet.add(u); urlCount.set(u, (urlCount.get(u) || 0) + 1); });
      const xlsxIdx = idToXlsxIdx.get(id);
      if (xlsxIdx === undefined) { willCreate.push({ pm, id }); return; }
      matched.push({ pm, pmIdx, id, xlsxIdx });
    });

    const unmatchedJson = labeledEntries.filter((e) => !usedIds.has(e.id));
    const commonUrls = new Set<string>();
    urlCount.forEach((n, u) => { if (n > 1) commonUrls.add(u); });
    let driveUrls = 0; let nonDriveUrls = 0;
    urlSet.forEach((u) => { if (commonUrls.has(u)) return; if (extractDriveFileId(u)) driveUrls++; else nonDriveUrls++; });

    return { matched, unmatchedKml, unmatchedJson, willCreate, placemarksWithoutGps, uniqueUrls: urlSet.size, driveUrls, nonDriveUrls, commonUrls };
  })();

  const excelOnly = !!xlsxRows && !kmlFile && !jsonFile;
  const ready = !!analysis || excelOnly;

  async function runImport() {
    if (!analysis && !excelOnly) return;
    if (excelOnly && !overwriteMode) {
      if (!window.confirm(t("importView.confirmAppend"))) return;
    } else {
      const phrase = t("importView.confirmPhrase");
      const typed = window.prompt(t("importView.confirmPrompt", { phrase }));
      if (typed == null) return;
      if (typed.trim() !== phrase) { window.alert(t("importView.confirmMismatch")); return; }
    }
    setBusy(true); setLog([]); setDone(false);
    try {
      let sid: string;
      if (overwriteMode) {
        if (!xlsxFile) throw new Error("No XLSX selected");
        if (!xlsxIsCanonicalOrder) { appendLog(t("importView.overwriteColumnOrderError")); setBusy(false); return; }
        if (!window.confirm(t("importView.confirmOverwrite"))) { appendLog("Overwrite cancelled."); setBusy(false); return; }
        appendLog("Overwriting sheet from XLSX (preserving formatting)…");
        try {
          sid = await replaceSpreadsheetFromXlsx(xlsxFile);
          appendLog("Sheet replaced from XLSX.");
        } catch (e) {
          if ((e as { code?: string }).code === "OVERWRITE_COLUMN_ORDER") appendLog(t("importView.overwriteColumnOrderError"));
          else appendLog(`Overwrite failed: ${(e as Error).message}`);
          setBusy(false); return;
        }
      } else {
        appendLog("Preparing Sheet…");
        sid = await ensureSpreadsheet();
      }

      const idCol = COL["ID"];
      const hasExtras = xlsxExtras.headers.length > 0;

      if (overwriteMode) {
        appendLog("XLSX content already loaded via overwrite — skipping append.");
      } else if (skipXlsx) {
        appendLog("Skipping XLSX append (checkbox).");
      } else {
        const existing = await readAllRows(sid);
        const startIndex = existing.length;
        const widen = hasExtras && xlsxHasAllRequired;
        if (widen) await extendHeaderRow(sid, xlsxExtras.headers);
        const newRows = (xlsxRows || []).map((row, i) => {
          const padded: unknown[] = [...row];
          while (padded.length < HEADERS.length) padded.push("");
          padded[0] = String(startIndex + i + 1);
          padded[COL["表記名①"]] = displayNameFormula(startIndex + i + 2);
          if (widen) padded.push(...xlsxExtras.rows[i]);
          return padded;
        });
        if (newRows.length > 0) {
          if (widen) await appendRowsWide(sid, newRows);
          else await appendRows(sid, newRows as Row[]);
          appendLog(widen ? `Appended ${newRows.length} rows from XLSX (+${xlsxExtras.headers.length} extra columns).` : `Appended ${newRows.length} rows from XLSX.`);
        } else {
          appendLog("No XLSX rows to append.");
        }
      }

      if (!placemarks || !labeledEntries) {
        appendLog("Excel-only import complete.");
        setDone(true);
        return;
      }

      let allRows = await readAllRows(sid);
      const idToRowIdx = new Map<string, number>();
      allRows.forEach((r, i) => { const id = String(r[idCol] || "").trim(); if (id && !idToRowIdx.has(id)) idToRowIdx.set(id, i); });

      const coordToId = buildCoordIndex(labeledEntries);
      const idsNeedingRow: string[] = [];
      const seenNew = new Set<string>();
      for (const pm of placemarks) {
        if (pm.lat == null || pm.lng == null) continue;
        const id = lookupCoord(coordToId, pm.lng, pm.lat);
        if (!id) continue;
        if (idToRowIdx.has(id) || seenNew.has(id)) continue;
        seenNew.add(id); idsNeedingRow.push(id);
      }
      if (idsNeedingRow.length > 0) {
        const base = allRows.length;
        const createdRows: Row[] = idsNeedingRow.map((id, i) => {
          const row: Row = new Array(HEADERS.length).fill("");
          row[0] = String(base + i + 1); row[idCol] = id;
          row[COL["表記名①"]] = displayNameFormula(base + i + 2);
          return row;
        });
        await appendRows(sid, createdRows);
        appendLog(`Created ${createdRows.length} new rows for IDs not in XLSX.`);
        allRows = await readAllRows(sid);
        idToRowIdx.clear();
        allRows.forEach((r, i) => { const id = String(r[idCol] || "").trim(); if (id && !idToRowIdx.has(id)) idToRowIdx.set(id, i); });
      }

      let pinsSkippedNoId = 0;
      const resolved: Array<{ pm: KmlPlacemark; id: string; rowIdx: number }> = [];
      for (const pm of placemarks) {
        if (pm.lat == null || pm.lng == null) { pinsSkippedNoId++; continue; }
        const id = lookupCoord(coordToId, pm.lng, pm.lat);
        if (!id) { pinsSkippedNoId++; appendLog(`Skip "${pm.name}" — no JSON ID for coords (${pm.lng}, ${pm.lat}).`); continue; }
        const rowIdx = idToRowIdx.get(id);
        if (rowIdx === undefined) { appendLog(`Skip ${id} — row creation failed unexpectedly.`); continue; }
        resolved.push({ pm, id, rowIdx });
      }

      let gpsWritten = 0; let gpsSkippedExisting = 0;
      const gpsBatch: Array<{ range: string; values: unknown[][] }> = [];
      for (const { pm, rowIdx } of resolved) {
        const existingLat = String(allRows[rowIdx][COL["緯度"]] || "").trim();
        const existingLng = String(allRows[rowIdx][COL["経度"]] || "").trim();
        if (existingLat && existingLng) { gpsSkippedExisting++; continue; }
        const latStr = String(pm.lat);
        const lngStr = String(pm.lng);
        gpsBatch.push({ range: cellRange(rowIdx, COL["緯度"]), values: [[latStr]] });
        gpsBatch.push({ range: cellRange(rowIdx, COL["経度"]), values: [[lngStr]] });
        allRows[rowIdx][COL["緯度"]] = latStr;
        allRows[rowIdx][COL["経度"]] = lngStr;
        gpsWritten++;
      }
      if (gpsBatch.length > 0) { await batchUpdateValues(sid, gpsBatch); appendLog(`GPS batch: wrote ${gpsWritten} pins in 1 call.`); }
      else appendLog("GPS batch: nothing to write.");

      const commonUrls = analysis!.commonUrls;
      const fileIdBatch: Array<{ range: string; values: unknown[][] }> = [];
      let pinsWithFileIds = 0; let pinsSkippedFileIdsExisting = 0;
      let urlsSkippedNonDrive = 0; let urlsSkippedCommon = 0;

      for (const { pm, rowIdx } of resolved) {
        const fileIds: string[] = [];
        const seen = new Set<string>();
        for (const url of pm.mediaUrls) {
          if (commonUrls.has(url)) { urlsSkippedCommon++; continue; }
          const fid = extractDriveFileId(url);
          if (!fid) { urlsSkippedNonDrive++; continue; }
          if (seen.has(fid)) continue;
          seen.add(fid); fileIds.push(fid);
        }
        if (fileIds.length === 0) continue;
        const existing = String(allRows[rowIdx][COL["写真ファイルID"]] || "").trim();
        if (existing) { pinsSkippedFileIdsExisting++; continue; }
        const value = fileIds.join(",");
        fileIdBatch.push({ range: cellRange(rowIdx, COL["写真ファイルID"]), values: [[value]] });
        allRows[rowIdx][COL["写真ファイルID"]] = value;
        pinsWithFileIds++;
      }
      if (fileIdBatch.length > 0) { await batchUpdateValues(sid, fileIdBatch); appendLog(`Photo file IDs: wrote ${pinsWithFileIds} pins in 1 call.`); }
      else appendLog("Photo file IDs: nothing to write.");

      appendLog(`Done. GPS written: ${gpsWritten} (skipped existing: ${gpsSkippedExisting}). File IDs written: ${pinsWithFileIds} pins (skipped existing: ${pinsSkippedFileIdsExisting}, common URLs filtered: ${urlsSkippedCommon}, non-Drive URLs ignored: ${urlsSkippedNonDrive}). Pins without JSON ID: ${pinsSkippedNoId}.`);
      setDone(true);
    } catch (e) {
      appendLog(`Fatal error: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
    {busy && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-4 select-none">
          <div className="relative">
            <img src={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png`} alt="MeW" className="w-16 h-16 opacity-90" />
            <span className="absolute inset-0 -m-2.5 rounded-full border-4 border-pink-200 border-t-pink-600 animate-spin" />
          </div>
          <p className="text-sm text-gray-500 tracking-wide">{t("common.processing")}</p>
        </div>
      </div>
    )}
    <div className="flex justify-end px-4 pt-2">{slider}</div>
    <div className="mx-auto p-4 space-y-4" style={{ maxWidth }}>
      <h2 className="text-xl font-bold flex items-center gap-2 text-gray-800">
        <i className="fa-solid fa-file-import text-pink-600" aria-hidden="true" />
        {t("importView.title")}
      </h2>
      <p className="text-sm text-gray-600">{t("importView.intro")}</p>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="border rounded-md p-4 bg-gray-50">
          <div className="font-semibold mb-2">{t("importView.xlsxFile")}</div>
          <input ref={xlsxRef} type="file" accept=".xlsx,.xls" onChange={(e) => handleXlsxPick(e.target.files?.[0])} className="block w-full text-sm" />
          {xlsxFile && <div className="text-xs text-gray-600 mt-2">{xlsxFile.name} — {xlsxRows ? t("importView.xlsxRowCount", { count: String(xlsxRows.length) }) : "…"}</div>}
        </div>
        <div className="border rounded-md p-4 bg-gray-50">
          <div className="font-semibold mb-2">{t("importView.kmlFile")}</div>
          <input ref={kmlRef} type="file" accept=".kml,application/vnd.google-earth.kml+xml" onChange={(e) => handleKmlPick(e.target.files?.[0])} className="block w-full text-sm" />
          {kmlFile && <div className="text-xs text-gray-600 mt-2">{kmlFile.name} — {placemarks ? t("importView.placemarkCount", { count: String(placemarks.length) }) : "…"}</div>}
        </div>
        <div className="border rounded-md p-4 bg-gray-50">
          <div className="font-semibold mb-2">{t("importView.jsonFile")}</div>
          <input ref={jsonRef} type="file" accept=".json,application/json" onChange={(e) => handleJsonPick(e.target.files?.[0])} className="block w-full text-sm" />
          {jsonFile && <div className="text-xs text-gray-600 mt-2">{jsonFile.name} — {labeledEntries ? t("importView.labeledCount", { count: String(labeledEntries.length) }) : "…"}</div>}
        </div>
      </div>

      {analysisError && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{analysisError}</div>}

      {analysis && (
        <div className="border rounded-md p-4 bg-white space-y-2 text-sm">
          <div className="font-semibold text-gray-800">{t("importView.preview")}</div>
          <ul className="space-y-1 text-gray-700">
            {skipXlsx ? (
              <li>{t("importView.pinsToProcess", { count: String(analysis.matched.length + analysis.willCreate.length) })}</li>
            ) : (
              <>
                <li>{t("importView.matched", { count: String(analysis.matched.length) })}</li>
                {analysis.willCreate.length > 0 && <li className="text-blue-700">+ {t("importView.willCreate", { count: String(analysis.willCreate.length) })}</li>}
              </>
            )}
            <li>{t("importView.unmatchedKml", { count: String(analysis.unmatchedKml.length) })}</li>
            <li>{t("importView.unmatchedJson", { count: String(analysis.unmatchedJson.length) })}</li>
            <li>{t("importView.uniqueUrls", { total: String(analysis.uniqueUrls), drive: String(analysis.driveUrls), other: String(analysis.nonDriveUrls) })}</li>
            {analysis.commonUrls.size > 0 && <li className="text-gray-600">{t("importView.commonUrls", { count: String(analysis.commonUrls.size) })}</li>}
            {analysis.placemarksWithoutGps > 0 && <li className="text-amber-700">⚠ {t("importView.placemarksWithoutGps", { count: String(analysis.placemarksWithoutGps) })}</li>}
          </ul>
          {analysis.unmatchedKml.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-gray-600">{t("importView.showUnmatchedKml")}</summary>
              <ul className="mt-1 ml-4 list-disc">
                {analysis.unmatchedKml.slice(0, 50).map((p, i) => <li key={i}>{p.name} ({p.lng}, {p.lat})</li>)}
                {analysis.unmatchedKml.length > 50 && <li>… +{analysis.unmatchedKml.length - 50} more</li>}
              </ul>
            </details>
          )}
          {analysis.willCreate.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-gray-600">{t("importView.showWillCreate")}</summary>
              <ul className="mt-1 ml-4 list-disc">
                {analysis.willCreate.slice(0, 50).map(({ id }, i) => <li key={i}>{id}</li>)}
                {analysis.willCreate.length > 50 && <li>… +{analysis.willCreate.length - 50} more</li>}
              </ul>
            </details>
          )}
        </div>
      )}

      {xlsxRows && xlsxExtras.headers.length > 0 && <div className="text-xs text-gray-600">{t("importView.extrasDetected", { names: xlsxExtras.headers.join(", ") })}</div>}
      {excelOnly && <div className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded p-3">{t("importView.excelOnlyHint")}</div>}

      <div className="flex items-center gap-3 flex-wrap">
        {xlsxHasAllRequired ? (
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={overwriteMode} onChange={(e) => setOverwriteMode(e.target.checked)} disabled={busy} />
            <span>{t("importView.overwriteCheckbox")}</span>
            <span className="text-xs text-gray-500">{t("importView.overwriteHint")}</span>
          </label>
        ) : (
          xlsxRows && <div className="text-xs text-gray-500">{t("importView.overwriteDisabledHint")}</div>
        )}
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={skipXlsx} onChange={(e) => setSkipXlsx(e.target.checked)} disabled={busy || overwriteMode} />
          <span>{t("importView.skipXlsx")}</span>
        </label>
        <button onClick={runImport} disabled={busy || !ready || !analysis}
          className="inline-flex items-center gap-2 bg-pink-600 text-white px-4 py-2 rounded-md hover:bg-pink-700 disabled:opacity-50">
          {busy ? <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" /> : <i className="fa-solid fa-cloud-arrow-up" aria-hidden="true" />}
          <span>{busy ? t("importView.running") : t("importView.confirm")}</span>
        </button>
      </div>

      {log.length > 0 && (
        <div className="border rounded-md bg-gray-900 text-green-200 text-xs font-mono p-3 max-h-80 overflow-auto">
          {log.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      )}

      {done && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-3">{t("importView.done")}</div>}
    </div>
    </>
  );
}
